// FastAPI hands back `detail` in three different shapes:
//   • a string        — HTTPException raised by our own routes
//   • an array        — 422 request validation, each entry {type, loc, msg, input, ctx}
//   • an object       — a few routes return structured payloads (e.g. {conflicts: [...]})
// Putting the array or object straight into JSX crashes the render
// ("Objects are not valid as a React child"), so error paths funnel through here.

const cleanMsg = (msg) =>
 typeof msg === "string"
  // Pydantic prefixes messages raised from validators; the prefix means nothing to a user.
  ? msg.replace(/^(Value error|Assertion failed),\s*/i, "").trim()
  : "";

// "body" / "__root__" carry no meaning for the user; a real field name does.
const fieldFromLoc = (loc) => {
 if (!Array.isArray(loc)) return "";
 const parts = loc.filter(
  (p) => typeof p === "string" && !["body", "query", "path", "__root__"].includes(p)
 );
 return parts.length ? parts[parts.length - 1] : "";
};

export const detailToMessage = (detail, fallback = "Something went wrong") => {
 if (typeof detail === "string") return detail.trim() || fallback;

 if (Array.isArray(detail)) {
  const msgs = detail
   .map((e) => {
    if (typeof e === "string") return e.trim();
    const msg = cleanMsg(e?.msg);
    if (!msg) return "";
    const field = fieldFromLoc(e?.loc);
    return field ? `${field}: ${msg}` : msg;
   })
   .filter(Boolean);
  return msgs.length ? msgs.join("; ") : fallback;
 }

 if (detail && typeof detail === "object") {
  const direct = cleanMsg(detail.msg) || cleanMsg(detail.message) || cleanMsg(detail.error);
  if (direct) return direct;
 }

 return fallback;
};

export const apiErrorMessage = (err, fallback = "Something went wrong") => {
 const detail = err?.response?.data?.detail;
 if (detail === undefined || detail === null) {
  const msg = err?.response?.data?.message;
  if (typeof msg === "string" && msg.trim()) return msg;
  return fallback;
 }
 return detailToMessage(detail, fallback);
};

export default apiErrorMessage;
